import { gsap } from 'gsap';
import { Container, Sprite, Ticker } from '../../odie';
import { app } from '../app';
import { IAnimation } from '../../lib/pattern/IAnimation';
import { tweenPower2In } from '../../lib/util/tweens';
import { CubeBeamAnimation } from './CubeBeamAnimation';
import { getMatch2AssetPath } from '../util/assets';
import { SuperCubeOverlayAnimation } from './SuperCubeOverlayAnimation';

// constants
//-----------------------------------------------------------------------------
const manifest = {
  super: 'power/block.cube.super.png',
  all: 'power/block.cube.all.png',
  blue: 'power/block.cube.blue.png',
  green: 'power/block.cube.green.png',
  purple: 'power/block.cube.purple.png',
  red: 'power/block.cube.red.png',
  yellow: 'power/block.cube.yellow.png',
  blueSparkle: 'assets/particles/sparkle_blue.png',
  greenSparkle: 'assets/particles/sparkle_green.png',
  purpleSparkle: 'assets/particles/sparkle_purple.png',
  redSparkle: 'assets/particles/sparkle_red.png',
  yellowSparkle: 'assets/particles/sparkle_yellow.png',
  sparkles: 'fx.cube/fx.cube.sparkles.png',
  wave: 'fx.bomb/fx.bomb.wave.png',
};

export class SuperCubeDoubleAnimation extends Container implements IAnimation {
  private cube?: SuperCubeOverlayAnimation;
  private scale = 3;

  constructor() {
    super();
  }

  public static assets(): string[] {
    return [...CubeBeamAnimation.assets(), ...Object.values(manifest)];
  }

  public async start(): Promise<void> {
    Ticker.system.add(this.update);
    app.sound.play('win');
    app.sound.play('puzzle-win-confetti');
    app.sound.play('puzzle-combo-cube-cube');
    await this.playCube();
  }

  public stop() {
    Ticker.system.remove(this.update);
    this.stopCube();
  }

  private update = (dt: number) => {
    this.cube?.update(dt);
  };

  private async playCube() {
    if (this.cube) return;
    this.cube = new SuperCubeOverlayAnimation();
    this.cube.wobble = 0.05;
    this.cube.zIndex = 1;
    this.addChild(this.cube);
    gsap.to(this.cube, {
      scaleX: this.scale,
      scaleY: this.scale,
      duration: 0.6,
      ease: 'back.out',
    });
    this.cube.start();
    this.animateChargeUpWaves();
    this.animateChargeUpSparkles();
  }

  private stopCube() {
    if (!this.cube) return;
    this.removeChild(this.cube);
    gsap.killTweensOf(this.cube);
    this.cube.stop();
    this.cube = undefined;
  }

  public async pulse() {
    if (!this.cube) return;
    app.sound.play('puzzle-combo-rocket-rocket');
    app.sound.play('puzzle-cube-clear');
    await gsap.to(this.cube, {
      scaleX: this.scale + 2,
      scaleY: this.scale + 2,
      duration: 0.1,
      ease: 'sine.out',
    });
    gsap.to(this.cube, {
      scaleX: this.scale,
      scaleY: this.scale,
      duration: 0.5,
      ease: 'back.out',
    });
  }

  private async animateChargeUpSparkles() {
    const promises: gsap.core.Timeline[] = [];

    for (let index = 0; index < 6; index++) {
      const sparkles = Sprite.from(manifest.sparkles);
      sparkles.alpha = 0;
      sparkles.anchorX = sparkles.anchorY = 0.5;
      sparkles.scaleX = sparkles.scaleY = 4 + Math.random();
      sparkles.rotation = index * 0.3;
      sparkles.zIndex = 2;
      this.addChild(sparkles);

      promises.push(
        gsap
          .timeline({ delay: index * 0.35 })
          .to(sparkles, { duration: 0.6, alpha: 1, ease: 'quad.in' })
          .to(sparkles, { duration: 1, scaleX: 0, scaleY: 0, ease: 'none' }, 0)
          .to(
            sparkles,
            {
              duration: 1,
              rotation: sparkles.rotation - Math.PI,
              ease: 'quad.in',
              onComplete: () => this.removeChild(sparkles),
            },
            0,
          ),
      );
    }

    await Promise.all(promises);
  }

  private async animateChargeUpWaves() {
    const ticker = { tick: 0 };
    const wave = Sprite.from(manifest.wave);

    wave.alpha = 0.1;
    wave.anchorX = wave.anchorY = 0.5;
    wave.scaleX = wave.scaleY = 3.5;
    wave.width = wave.height = 300;
    this.addChild(wave);

    const waveDuration = 0.4;
    const fadeInDuration = 0.3;
    const duration = waveDuration * 7 - 0.1;

    await gsap.to(ticker, {
      duration,
      tick: duration,
      ease: 'quad.in',
      onUpdate: () => {
        const ratio = (ticker.tick % waveDuration) / waveDuration;
        const inverseRatio = 1 - ratio;
        wave.scaleX = wave.scaleY = inverseRatio * 3.5;
        wave.alpha = Math.min(ratio / fadeInDuration, 1);
      },
    });

    this.removeChild(wave);
  }

  public async explode() {
    if (this.cube) {
      await gsap.to(this.cube, {
        scaleX: 1,
        scaleY: 1,
        duration: 0.3,
        ease: 'back.in',
      });
      this.stopCube();
    }

    app.sound.play('puzzle-cube-dismiss', { volume: 0.25 });

    const textures = [
      manifest.blueSparkle,
      manifest.greenSparkle,
      manifest.purpleSparkle,
      manifest.redSparkle,
      manifest.yellowSparkle,
    ].map((path) => getMatch2AssetPath(path));

    // determine scale
    const scale = 1.75;

    // determine magnitude
    const magnitudeLow = 300;
    const magnitudeHigh = 700;

    // determine duration
    const duration = 1;

    const rate = 0.005;
    const limit = 65;
    const emitterDuration = 0.25;

    // create emitter
    const emitter = app.particles.create({
      textures,
      rate,
      limit,
      emitterDuration,
      scale: [
        { x: 0.5, y: 0.5 },
        { x: scale, y: scale },
      ],
      duration,
      behaviors: [
        { type: 'explode', magnitude: [magnitudeLow, magnitudeHigh] },
        { type: 'kinematic' },
        { type: 'drag', drag: 2 },
        { type: 'fade', to: 0, tween: tweenPower2In },
      ],
    });

    this.addChild(emitter.view);
    await emitter.start();
    this.removeChild(emitter.view);
  }
}
